import type {
  EditVehicleExpirationPromptResolve,
  VehicleExpirationNode,
} from '@/modules/vehicles'
import { EditVehicleExpirationModal } from '@/modules/vehicles/fleet/components/EditVehicleExpirationModal'
import {
  useVehicleExpirationMutations,
  useVehicleServicePlanConversionMutations,
} from '@/modules/vehicles/fleet/mutations.hooks'
import { getExpirationAndNotificationTime } from '@/modules/vehicles/list-filters'
import { format } from '@/utils/date-fns'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { getVehicleExpirationTypeName } from '../util'

/** Edit/delete/move handlers for the vehicle expiration dates page. */
export function useVehicleExpirationHandlers() {
  const translations = useTranslate({
    delete: 'vehicles.prompts.delete-expiration-date',
    deleteConfirmation: [
      'documents.prompts.delete-document-confirmation',
      { name: '' },
    ],
    moveToServices: 'vehicles.expirations.move-to-services',
    moveConfirmation: [
      'vehicles.prompts.move-to-services-confirmation',
      { name: '' },
    ],
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const { patchExpiration, deleteExpiration } = useVehicleExpirationMutations()
  const { convertExpirationToServicePlan } =
    useVehicleServicePlanConversionMutations()

  async function editExpiration(expiration: VehicleExpirationNode) {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => (
        <EditVehicleExpirationModal
          vehicleExpiration={expiration}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    if (!expirationDate) return

    await patchExpiration({
      variables: {
        id: expiration.id,
        input: {
          description: data.form.description,
          expirationType: data.form.expirationType,
          expirationDate,
          notificationTime,
          responsibleUser: data.form.responsibleUser,
        },
      },
    })
  }

  async function removeExpiration(expiration: VehicleExpirationNode) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: describe(expiration) }),
      translations.delete
    )
    if (!answer) return

    await deleteExpiration({ variables: { id: expiration.id } })
  }

  /**
   * Move a row the FD-571 migration filed as an expiration onto the services
   * page. The migration classified by provenance, which is a guess about intent
   * -- a workshop visit booked under a built-in type looks like a deadline.
   */
  async function moveToServices(expiration: VehicleExpirationNode) {
    const { data: answer } = await confirm(
      translations.moveConfirmation({ name: describe(expiration) }),
      translations.moveToServices
    )
    if (!answer) return

    await convertExpirationToServicePlan({ variables: { id: expiration.id } })
  }

  return { editExpiration, removeExpiration, moveToServices }
}

function describe(expiration: VehicleExpirationNode) {
  return `${format(
    new Date(expiration.expirationDate),
    'PP'
  )} - ${getVehicleExpirationTypeName({ expiration })}`
}
